import React, { useState, FocusEvent } from 'react';

import isEmpty from 'lodash/isEmpty';

import { useClassnames } from '@lumapps/classnames';
import { get } from '@lumapps/constants';
import { sanitizeLoginStepFormUrl } from '@lumapps/login/utils/urlSanitation';
import { mdiEyeOutline } from '@lumapps/lumx/icons';
import { TextField, Button, Icon, Size, Link } from '@lumapps/lumx/react';
import { useQueryParams } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { isValidEmail } from '@lumapps/utils/string/isValidEmail';

import { SIGN_IN } from '../../keys';
import { STEPS } from '../../routes';
import { LoginMessage } from '../LoginMessage';

import './index.scss';

export interface SignInFormProps {
    defaultEmail?: string;
    defaultPassword?: string;
    goToNextStep?: () => void;
}
const CLASSNAME = 'sign-in-form';

/**
 * Users have to be 1-32 characters (inclusive), alphanumerics (a-zA-Z0-9) and can use underscores.
 */
const USERNAME_REGEX = /^[a-zA-Z0-9_]{1,32}$/;

export const SignInForm: React.FC<SignInFormProps> = ({ defaultEmail = '', defaultPassword = '', goToNextStep }) => {
    const { translateKey } = useTranslate();
    const { r: redirectUri } = useQueryParams();
    const { element, block } = useClassnames(CLASSNAME);
    const [errorLogin, setErrorLogin] = useState<string>('');
    const [login, setLogin] = useState<string>(defaultEmail);
    const [password, setPassword] = useState<string>(defaultPassword);
    const [isPasswordHidden, setPasswordHidden] = useState<boolean>(true);
    const Config = get();
    const { loginUsernameEnabled } = Config;
    const isLoginUserNameEnabled = String(loginUsernameEnabled) === 'True';

    /**
     * Checks if the value is either a valid email,
     * or a username matching the strict regex (1-33 chars, alphanum + _)
     */
    const isValidLogin = (value: string): boolean => {
        if (!value) {
            return false;
        }
        if (!isLoginUserNameEnabled) {
            return isValidEmail(value);
        }
        return USERNAME_REGEX.test(value) || isValidEmail(value);
    };

    /**
     * Prevent form submission (POST) without a valid login (email or username)
     * when the user validates the form with the ENTER key.
     * @param evt
     */
    const handleSubmit = (evt: React.SyntheticEvent) => {
        if (!isValidLogin(login)) {
            evt.preventDefault();
        }
    };

    const controlLoginInput = (evt: FocusEvent<HTMLInputElement>) => {
        if (isEmpty(evt)) {
            return;
        }

        if (!isValidLogin(login)) {
            const errorKey = isLoginUserNameEnabled
                ? SIGN_IN.INVALID_EMAIL_ADDRESS_OR_USERNAME
                : SIGN_IN.INVALID_EMAIL_ERROR;
            setErrorLogin(translateKey(errorKey));
        } else {
            setErrorLogin('');
        }
    };

    return (
        <>
            <form
                className={block()}
                action={sanitizeLoginStepFormUrl(STEPS.SIGN_IN, window.location.pathname, window.location.search)}
                method="POST"
                onSubmit={handleSubmit}
            >
                <TextField
                    label={
                        !isLoginUserNameEnabled
                            ? translateKey(GLOBAL.EMAIL)
                            : translateKey(SIGN_IN.EMAIL_ADDRESS_OR_USERNAME)
                    }
                    error={errorLogin}
                    hasError={!!errorLogin}
                    name="login"
                    id="login"
                    value={login}
                    onChange={setLogin}
                    onBlur={controlLoginInput}
                    className={element('first-input')}
                    autoComplete="email"
                />

                <TextField
                    label={translateKey(SIGN_IN.ADMIN_USER_DIRECTORY_PASSWORD)}
                    className={element('input')}
                    type={isPasswordHidden ? 'password' : 'text'}
                    name="password"
                    id="password"
                    value={password}
                    onChange={setPassword}
                    autoComplete="current-password"
                    afterElement={
                        <Icon
                            className={element('eye-password-icon')}
                            icon={mdiEyeOutline}
                            size={Size.xxs}
                            onClick={() => {
                                setPasswordHidden(!isPasswordHidden);
                            }}
                        />
                    }
                />

                <input id="redirectUri" type="hidden" name="redirectUri" value={redirectUri} />

                <input id="loginMethod" type="hidden" name="loginMethod" value="email" />

                <Button
                    disabled={!password || !isValidLogin(login)}
                    className={element('button-form')}
                    type="submit"
                    fullWidth
                >
                    {translateKey(GLOBAL.SIGN_IN)}
                </Button>

                <LoginMessage />
            </form>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link className={element('forgot-password-link')} onClick={goToNextStep} typography="body1">
                {translateKey(SIGN_IN.FORGOT_YOUR_PASSWORD)}
            </Link>
        </>
    );
};
